import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Link } from 'react-router-dom';
import { Users, Building2, HardHat, Shield, Briefcase, Wrench, Zap, Settings, CheckCircle, ArrowRight } from 'lucide-react';

const managerialRoles = [
  { icon: HardHat, title: 'Capi Cantiere / Site Supervisor', description: 'Gestione operativa del cantiere e coordinamento delle attività' },
  { icon: Settings, title: 'Commissioning Manager / Supervisor', description: 'Supervisione delle fasi di avviamento e collaudo impianti' },
  { icon: Shield, title: 'HSE Manager / Supervisor', description: 'Gestione della sicurezza e conformità normativa' },
  { icon: Briefcase, title: 'Project Manager / Project Engineer', description: 'Pianificazione e controllo dei progetti complessi' },
  { icon: CheckCircle, title: 'Quality Inspector', description: 'Controllo qualità e ispezioni tecniche' }
];

const networkSpecializations = [
  { icon: Building2, title: 'Carpenteria metallica e strutturale', description: 'Lavorazioni metalliche di precisione' },
  { icon: Wrench, title: 'Montaggi meccanici', description: 'Installazione e assemblaggio macchinari' },
  { icon: Zap, title: 'Impianti elettrici e strumentali', description: 'Realizzazione impianti elettrici industriali' },
  { icon: Settings, title: 'Saldature certificate', description: 'Saldature specializzate con certificazioni' },
  { icon: Settings, title: 'Manutenzione impiantistica', description: 'Servizi di manutenzione programmata e straordinaria' }
];

export default function ServiziPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  return (
    <div data-testid="servizi-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="hero-dark py-20 md:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="accent-line mb-8"></div>
            <h1 data-testid="servizi-headline" className="text-4xl md:text-6xl font-extrabold text-white mb-6">
              I Nostri Servizi
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl">
              Soluzioni di intermediazione specializzate per il settore EPC e industriale. 
              Dalla selezione di figure manageriali al network di imprese qualificate.
            </p>
          </div>
        </section>

        {/* Figure Manageriali Section */}
        <section id="manageriali" data-testid="manageriali-section" className="py-20 md:py-32 bg-[#F5F5F7] noise-bg relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
              <div className="md:col-span-4">
                <Users className="w-16 h-16 text-[#E94560] mb-6" strokeWidth={1.5} />
                <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mb-4">
                  Figure Manageriali Specializzate
                </h2>
                <p className="text-[#555555] mb-8">
                  Mettiamo a disposizione figure tecniche e manageriali di alto profilo, 
                  selezionate e certificate per garantire risultati immediati.
                </p>
                <Link to="/lavora-con-noi/tecnici" className="btn-primary inline-flex items-center gap-2">
                  Richiedi Disponibilità
                  <ArrowRight size={20} />
                </Link>
              </div>
              <div className="md:col-span-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {managerialRoles.map((role, index) => (
                    <div 
                      key={index}
                      data-testid={`role-card-${index}`}
                      className="card-industrial p-6"
                    >
                      <role.icon className="w-10 h-10 text-[#27AE60] mb-4" strokeWidth={1.5} />
                      <h3 className="font-bold text-[#1A1A2E] mb-2">{role.title}</h3>
                      <p className="text-sm text-[#888888]">{role.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Network Imprese Section */}
        <section id="network" data-testid="network-section" className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
              <div className="md:col-span-8 md:order-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {networkSpecializations.map((spec, index) => (
                    <div 
                      key={index}
                      data-testid={`spec-card-${index}`}
                      className="card-industrial p-6"
                    >
                      <spec.icon className="w-10 h-10 text-[#E94560] mb-4" strokeWidth={1.5} />
                      <h3 className="font-bold text-[#1A1A2E] mb-2">{spec.title}</h3>
                      <p className="text-sm text-[#888888]">{spec.description}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="md:col-span-4 md:order-2">
                <Building2 className="w-16 h-16 text-[#E94560] mb-6" strokeWidth={1.5} />
                <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mb-4">
                  Network di Imprese Specializzate
                </h2>
                <p className="text-[#555555] mb-8">
                  Un network consolidato di imprese subappaltatrici qualificate e certificate, 
                  pronte ad intervenire sui tuoi progetti.
                </p>
                <Link to="/contatti" className="btn-primary inline-flex items-center gap-2">
                  Contattaci
                  <ArrowRight size={20} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Modello Flessibile Section */}
        <section className="py-20 md:py-32 bg-[#1A1A2E]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Modello Flessibile
              </h2>
              <p className="text-gray-300 text-lg mb-8">
                Offriamo soluzioni personalizzate in base alle tue esigenze. 
                Dal time & material alla forfait, dalla somministrazione al subappalto diretto. 
                La flessibilità è il nostro punto di forza.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {['Time & Material', 'Forfait', 'Somministrazione', 'Subappalto'].map((model, i) => (
                  <div key={i} className="bg-white/10 p-6 border border-white/20">
                    <span className="text-white font-bold">{model}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
